/* eslint-disable @typescript-eslint/no-unused-vars */
import { createApi } from "@reduxjs/toolkit/query/react";
import { components, operations } from "../../types/api";
import { baseQueryWithReauth } from "../../utils/baseQuery";

type Company = components["schemas"]["Company"];
type CompanyBilling = components["schemas"]["CompanyBilling"];
type CreateCompanyRequest =
  operations["CreateCompany"]["requestBody"]["content"]["application/json"];
type UpdateCompanyRequest =
  operations["UpdateCompany"]["requestBody"]["content"]["application/json"];
type GetCompaniesResponse =
  components["schemas"]["ApiResponse__40_Company-and-_billings-CompanyBilling-Array--assignments-CompanyAssignmentsDto__41_-Array_"];
type UserCompaniesResponse =
  components["schemas"]["ApiResponse__40_Company-and-_billings-CompanyBilling-Array__41_-Array_"];
type CompanyResponse =
  components["schemas"]["ApiResponse_Company-and-_users_58__40_CompanyUser-and-_user-User__41_-Array--billings-CompanyBilling-Array--assignments-CompanyAssignmentsDto__"];
// Responsables de la empresa: account / partner success (varias personas) y uno por
// plataforma. No dan acceso a la empresa: eso siguen siendo los usuarios asignados.
export type CompanyAssignments = components["schemas"]["CompanyAssignmentsDto"];
export type AssignedUser = components["schemas"]["AssignedUserDto"];
export type AssignablePlatform = components["schemas"]["Platform"];
// Quién paga por defecto cada plataforma: rellena el pago de inversión de las líneas nuevas.
export type InvestmentPayment = components["schemas"]["InvestmentPayment"];
type CompanyAssignmentsResponse =
  components["schemas"]["ApiResponse_CompanyAssignmentsDto_"];
type UpdateCompanyAssignmentsRequest =
  components["schemas"]["UpdateCompanyAssignmentsInput"];
type BulkDeleteResponse =
  components["schemas"]["ApiResponse_BulkDeleteResult_"];
type AssignUsersResponse =
  components["schemas"]["ApiResponse__success-boolean--assignedCount-number--company-Company-and-_users_58__40_CompanyUser-and-_user-User__41_-Array___"];
type RemoveUsersResponse =
  components["schemas"]["ApiResponse__success-boolean--removedCount-number--company-Company-and-_users_58__40_CompanyUser-and-_user-User__41_-Array___"];
// Bulk actions — no generated schema yet, typed inline ({ status, data, message })
type BulkAssignUsersResponse = {
  status: string;
  data: { assignedCount: number; companyCount: number };
  message?: string;
};
type BulkPaymentTermsResponse = {
  status: string;
  data: { updatedCount: number };
  message?: string;
};
type CompanyBillingResponse =
  components["schemas"]["ApiResponse_CompanyBilling_"];
type HoldedSyncSummaryResponse =
  components["schemas"]["ApiResponse_HoldedSyncSummary_"];
// Contactos de Holded: solo lectura, para rellenar los datos fiscales al dar de
// alta una empresa en vez de teclearlos y que luego el sync los sobrescriba.
export type HoldedContactSummary =
  components["schemas"]["HoldedContactSummary"];
export type HoldedContactPreview =
  components["schemas"]["HoldedContactPreview"];
// `data` es el array de contactos, como siempre, y el aviso viaja como campo hermano:
// meterlo dentro de `data` rompio esta pantalla en cuanto se desplego, porque los
// navegadores con el bundle anterior en cache seguian tratando `data` como un array.
type HoldedContactsResponse =
  components["schemas"]["ApiResponseWithWarning_HoldedContactSummary-Array_"];
export type HoldedCatalogueDiagnostics =
  components["schemas"]["HoldedCatalogueDiagnostics"];
type HoldedDiagnosticsResponse =
  components["schemas"]["ApiResponse_HoldedCatalogueDiagnostics_"];
type HoldedContactPreviewResponse =
  components["schemas"]["ApiResponse_HoldedContactPreview_"];
type CreateCompanyBillingRequest =
  components["schemas"]["Omit_CompanyBilling.id-or-createdAt-or-updatedAt-or-companyId_"];
type UpdateCompanyBillingRequest =
  components["schemas"]["Partial_Omit_CompanyBilling.id-or-createdAt-or-updatedAt-or-companyId__"];

// Audience types — using generated TSOA types
export type CompanyAudience = components["schemas"]["CompanyAudience"];
type CompanyAudienceResponse =
  components["schemas"]["ApiResponse_CompanyAudience_"];
type CompanyAudiencesResponse =
  components["schemas"]["ApiResponse_CompanyAudience-Array_"];
type CreateAudienceRequest = {
  name: string;
  description?: string;
  sex?: "MAN" | "WOMAN" | "BOTH";
  minimumAge?: number;
  maximumAge?: number;
  language?: string;
  geography?: string;
  technology?: string;
  interests?: string;
  dataAudience?: string;
  ctr?: number;
  exclusions?: string;
  notes?: string;
  imageUrl?: string;
};
type UpdateAudienceRequest = {
  name?: string;
  description?: string | null;
  sex?: "MAN" | "WOMAN" | "BOTH" | null;
  minimumAge?: number | null;
  maximumAge?: number | null;
  language?: string | null;
  geography?: string | null;
  technology?: string | null;
  interests?: string | null;
  dataAudience?: string | null;
  ctr?: number | null;
  exclusions?: string | null;
  notes?: string | null;
  imageUrl?: string | null;
};

export const companyApi = createApi({
  reducerPath: "companyApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Companies",
    "CompanyBillings",
    "CompanyAudiences",
    "CompanyAssignments",
  ],
  endpoints: (builder) => ({
    getCompanies: builder.query<GetCompaniesResponse, void>({
      query: () => "/companies",
      providesTags: ["Companies"],
    }),
    getCompanyById: builder.query<CompanyResponse, string>({
      query: (id) => `/companies/${id}`,
      providesTags: ["Companies"],
      keepUnusedDataFor: 0,
    }),
    createCompany: builder.mutation<CompanyResponse, CreateCompanyRequest>({
      query: (newCompany) => ({
        url: "/companies",
        method: "POST",
        body: newCompany,
      }),
      invalidatesTags: ["Companies"],
    }),
    updateCompany: builder.mutation<
      CompanyResponse,
      { id: string; updates: UpdateCompanyRequest }
    >({
      query: ({ id, updates }) => ({
        url: `/companies/${id}`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: ["Companies"],
    }),
    deleteCompany: builder.mutation<CompanyResponse, string>({
      query: (id) => ({
        url: `/companies/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Companies"],
    }),
    bulkDeleteCompanies: builder.mutation<
      BulkDeleteResponse,
      { ids: string[] }
    >({
      query: ({ ids }) => ({
        url: `/companies/bulk`,
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["Companies"],
    }),
    assignUsersToCompany: builder.mutation<
      AssignUsersResponse,
      { id: string; userIds: string[] }
    >({
      query: ({ id, userIds }) => ({
        url: `/companies/${id}/assign-users`,
        method: "POST",
        body: { userIds },
      }),
      invalidatesTags: ["Companies"],
    }),
    removeUsersFromCompany: builder.mutation<
      RemoveUsersResponse,
      { id: string; userIds: string[] }
    >({
      query: ({ id, userIds }) => ({
        url: `/companies/${id}/remove-users`,
        method: "DELETE",
        body: { userIds },
      }),
      invalidatesTags: ["Companies"],
    }),

    // Bulk assign users to multiple companies
    bulkAssignUsers: builder.mutation<
      BulkAssignUsersResponse,
      { companyIds: string[]; userIds: string[] }
    >({
      query: (body) => ({
        url: "/companies/bulk-assign-users",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Companies"],
    }),

    // Bulk set/clear payment terms on multiple companies
    bulkSetPaymentTerms: builder.mutation<
      BulkPaymentTermsResponse,
      { companyIds: string[]; paymentTerms: string | null }
    >({
      query: (body) => ({
        url: "/companies/bulk-payment-terms",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Companies"],
    }),

    // Get companies accessible by the current user
    getUserCompanies: builder.query<UserCompaniesResponse, void>({
      query: () => "/companies/user-companies",
      providesTags: ["Companies"],
    }),

    // Get companies a given user is assigned to
    getCompaniesByUserId: builder.query<UserCompaniesResponse, string>({
      query: (userId) => `/companies/by-user/${userId}`,
      providesTags: ["Companies"],
    }),

    // GET /api/companies/{id}/assignments — quién lleva la empresa.
    getCompanyAssignments: builder.query<CompanyAssignmentsResponse, string>({
      query: (companyId) => `/companies/${companyId}/assignments`,
      providesTags: (_result, _error, companyId) => [
        { type: "CompanyAssignments", id: companyId },
      ],
    }),
    // PUT /api/companies/{id}/assignments — partnerSuccessUserIds sustituye la lista
    // entera; en platformOwners solo cambian las plataformas que van, y null la vacía.
    updateCompanyAssignments: builder.mutation<
      CompanyAssignmentsResponse,
      { companyId: string; body: UpdateCompanyAssignmentsRequest }
    >({
      query: ({ companyId, body }) => ({
        url: `/companies/${companyId}/assignments`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { companyId }) => [
        "Companies",
        { type: "CompanyAssignments", id: companyId },
      ],
    }),

    // Company Billing endpoints
    getCompanyBillings: builder.query<CompanyBillingResponse, string>({
      query: (companyId) => `/companies/${companyId}/billings`,
      providesTags: ["CompanyBillings"],
    }),
    createCompanyBilling: builder.mutation<
      CompanyBillingResponse,
      { companyId: string; billing: CreateCompanyBillingRequest }
    >({
      query: ({ companyId, billing }) => ({
        url: `/companies/${companyId}/billings`,
        method: "POST",
        body: billing,
      }),
      invalidatesTags: ["Companies", "CompanyBillings"],
    }),
    updateCompanyBilling: builder.mutation<
      CompanyBillingResponse,
      { billingId: string; updates: UpdateCompanyBillingRequest }
    >({
      query: ({ billingId, updates }) => ({
        url: `/companies/billings/${billingId}`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: ["Companies", "CompanyBillings"],
    }),
    deleteCompanyBilling: builder.mutation<void, string>({
      query: (billingId) => ({
        url: `/companies/billings/${billingId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Companies", "CompanyBillings"],
    }),

    // POST /api/companies/billings/{billingId}/sync-holded — sobrescribe los
    // datos fiscales del billing desde Holded. 400 si no tiene holdedContactId.
    syncBillingFromHolded: builder.mutation<CompanyBillingResponse, string>({
      query: (billingId) => ({
        url: `/companies/billings/${billingId}/sync-holded`,
        method: "POST",
      }),
      invalidatesTags: ["Companies", "CompanyBillings"],
    }),

    // POST /api/companies/billings/sync-holded-all — sincroniza todos los
    // billings con holdedContactId (solo ADMIN). Devuelve un resumen.
    syncAllBillingsFromHolded: builder.mutation<
      HoldedSyncSummaryResponse,
      void
    >({
      query: () => ({
        url: `/companies/billings/sync-holded-all`,
        method: "POST",
      }),
      invalidatesTags: ["Companies", "CompanyBillings"],
    }),

    // GET /api/companies/holded/contacts — busca contactos de Holded por razón
    // social, NIF o email en todas las cuentas configuradas.
    searchHoldedContacts: builder.query<
      HoldedContactsResponse,
      { search?: string; limit?: number }
    >({
      query: ({ search, limit }) => {
        const params = new URLSearchParams();
        if (search?.trim()) params.append("search", search.trim());
        if (limit) params.append("limit", String(limit));
        const queryString = params.toString();
        return `/companies/holded/contacts${
          queryString ? `?${queryString}` : ""
        }`;
      },
    }),

    // GET /api/companies/holded/diagnostics — de donde sale el catalogo, cuenta por
    // cuenta. Solo ADMIN. Refresca siempre, asi que tambien fuerza la recarga cuando
    // alguien acaba de crear un contacto en Holded y no le aparece.
    getHoldedDiagnostics: builder.query<HoldedDiagnosticsResponse, void>({
      query: () => "/companies/holded/diagnostics",
    }),

    // GET /api/companies/holded/contacts/{contactId} — datos fiscales del
    // contacto sin escribir en BD, para previsualizarlos antes de guardar.
    getHoldedContactPreview: builder.query<
      HoldedContactPreviewResponse,
      string
    >({
      query: (contactId) =>
        `/companies/holded/contacts/${encodeURIComponent(contactId)}`,
    }),

    // Company Audience endpoints
    getCompanyAudiences: builder.query<CompanyAudiencesResponse, string>({
      query: (companyId) => `/companies/${companyId}/audiences`,
      providesTags: ["CompanyAudiences"],
    }),
    createCompanyAudience: builder.mutation<
      CompanyAudienceResponse,
      { companyId: string; audience: CreateAudienceRequest }
    >({
      query: ({ companyId, audience }) => ({
        url: `/companies/${companyId}/audiences`,
        method: "POST",
        body: audience,
      }),
      invalidatesTags: ["CompanyAudiences"],
    }),
    updateCompanyAudience: builder.mutation<
      CompanyAudienceResponse,
      { audienceId: string; updates: UpdateAudienceRequest }
    >({
      query: ({ audienceId, updates }) => ({
        url: `/companies/audiences/${audienceId}`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: ["CompanyAudiences"],
    }),
    deleteCompanyAudience: builder.mutation<void, string>({
      query: (audienceId) => ({
        url: `/companies/audiences/${audienceId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CompanyAudiences"],
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  useGetCompanyByIdQuery,
  useGetUserCompaniesQuery,
  useGetCompaniesByUserIdQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useBulkDeleteCompaniesMutation,
  useAssignUsersToCompanyMutation,
  useRemoveUsersFromCompanyMutation,
  useBulkAssignUsersMutation,
  useBulkSetPaymentTermsMutation,
  useGetCompanyAssignmentsQuery,
  useUpdateCompanyAssignmentsMutation,
  useGetCompanyBillingsQuery,
  useCreateCompanyBillingMutation,
  useUpdateCompanyBillingMutation,
  useDeleteCompanyBillingMutation,
  useSyncBillingFromHoldedMutation,
  useSyncAllBillingsFromHoldedMutation,
  useSearchHoldedContactsQuery,
  useLazyGetHoldedDiagnosticsQuery,
  useLazySearchHoldedContactsQuery,
  useLazyGetHoldedContactPreviewQuery,
  useGetCompanyAudiencesQuery,
  useCreateCompanyAudienceMutation,
  useUpdateCompanyAudienceMutation,
  useDeleteCompanyAudienceMutation,
} = companyApi;
